import { Control, useForm } from "react-hook-form";
import { requireRule } from "src/components/common/forms/validation.ts";
import i18n from "i18next";
import { SystemDiagram } from "src/api/generated/postgres/db/types/public_/tables/systemDiagram.ts";
import { AavoForm } from "src/components/common/forms/AavoForm.tsx";
import { FormTextField } from "src/components/common/forms/fields/FormTextField.tsx";
import { FormCommonProps } from "src/components/common/forms/types.ts";
import { FormSelectField } from "src/components/common/forms/fields/FormSelectField.tsx";
import {
	getSystemDiagramDirectionLabels
} from "src/api/generated/postgres/db/types/public_/enums/systemDiagramDirection.ts";
import { enumOptions } from "src/components/common/forms/fields/selectionFieldUtils.ts";
import {
	getSystemDiagramContextTypeLabels,
	SystemDiagramContextType
} from "src/api/generated/postgres/db/types/public_/enums/systemDiagramContextType.ts";
import { SitesApi } from "src/api/generated/erp/common/sites/sitesApi.ts";
import { useAsyncFetch } from "src/utils/async/asyncFetch.ts";
import { Site } from "src/api/generated/erp/db/types/tables/site.ts";
import { AsyncRender } from "src/components/common/async/AsyncRender";
import { SystemDiagramCrudApi } from "src/api/generated/systemDiagrams/management/systemDiagramCrudApi.ts";

export interface SystemDiagramFormProps extends FormCommonProps<number> {
	systemDiagram: SystemDiagram | undefined;
}

export const SystemDiagramForm = (
	{
		systemDiagram,
		onFormEdited,
		...other
	}: SystemDiagramFormProps) => {

	const isNewRecord = systemDiagram === undefined;

	const {
		control,
		handleSubmit,
		watch,
		formState: { isDirty }
	} = useForm<SystemDiagram>({
		mode: "all",
		defaultValues: systemDiagram ?? {
			name: "",
			diagramDirection: "LEFT_TO_RIGHT"
		}
	});

	if (onFormEdited) watch(onFormEdited);

	const [sitesAsync] = useAsyncFetch(() => SitesApi.getAll());
	if (sitesAsync.data === undefined)
		return <AsyncRender asyncData={sitesAsync} />;
	const siteOptions = sitesAsync.data;

	const submitForm = async (values: SystemDiagram) => {
		const saved = await SystemDiagramCrudApi.save({ systemDiagram: values });
		return saved.systemDiagramId;
	};

	return (
		<AavoForm
			handleSubmit={handleSubmit}
			isDirty={isDirty}
			submit={submitForm}
			columns={1}
			fitContent={true}
			{...other}
		>
			<FormTextField
				control={control}
				name={"name"}
				rules={requireRule()}
				label={i18n.t("name")}
				autoFocus
			/>
			<FormSelectField
				control={control}
				name={"diagramDirection"}
				rules={requireRule()}
				label={i18n.t("diagram_direction")}
				options={enumOptions(getSystemDiagramDirectionLabels())}
			/>
			<FormSelectField
				control={control}
				name={"contextType"}
				rules={requireRule()}
				label={i18n.t("context")}
				disabled={!isNewRecord}
				options={enumOptions(getSystemDiagramContextTypeLabels())}
			/>
			<ContextParamFields control={control}
								contextType={watch("contextType")}
								siteOptions={siteOptions}
								isNewRecord={isNewRecord}
			/>
		</AavoForm>
	);

};

const ContextParamFields = (
	{
		isNewRecord,
		control,
		contextType,
		siteOptions
	}: {
		isNewRecord: boolean,
		control: Control<SystemDiagram>,
		contextType: SystemDiagramContextType,
		siteOptions: Site[]
	}) => {

	if (contextType === "MANUFACTURING" || contextType === "SALES") {
		return <FormSelectField
			control={control}
			name={"contextParam1"}
			label={i18n.t("site")}
			rules={requireRule()}
			disabled={!isNewRecord}
			options={siteOptions.map((site) => ({
				key: site.siteId.toString(),
				label: site.siteName
			}))}
		/>;
	}

	return null;
};